import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mic, Trophy, Sparkles, Play, Pause, VolumeX, Volume2 } from "lucide-react";
import { LEGENDS } from "@/data/legends";
import { usePlayer } from "@/contexts/PlayerContext";

const HERO_IMG =
  "https://images.unsplash.com/photo-1636226570637-3fbda7ca09dc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxtdXNpYyUyMHByb2R1Y2VyJTIwc3R1ZGlvfGVufDB8fHx8MTc4Mjc4MzIwOHww&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const {
    ambientOn, ambientPlaying, ambientTrack, ambientMuted,
    toggleAmbient, nextAmbient, autoStartAmbient, unmuteAmbient,
  } = usePlayer();

  // Attempt muted autoplay when landing mounts. Browsers permit muted autoplay.
  useEffect(() => {
    autoStartAmbient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="landing-page">
      {/* Hero */}
      <section className="relative border-b border-border overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/80 to-background" />
        </div>
        <div className="relative px-6 md:px-12 py-24 md:py-32 max-w-7xl mx-auto">
          <div className="font-mono-acc text-xs text-secondary flex items-center gap-2">
            <span className="rec-dot" /> NOW RECORDING — SEASON 01
          </div>
          <h1 className="font-display text-6xl md:text-8xl lg:text-9xl leading-[0.85] mt-6 max-w-4xl">
            Beats that <span className="text-primary">hit harder</span> than the algorithm.
          </h1>
          <p className="mt-8 text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
            The underground arena for producers. Drop a beat, get the vote, climb the bracket.
            Real heads judge. No labels. No gatekeepers.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              data-testid="cta-join"
              to="/register"
              className="font-mono-acc text-xs px-6 py-4 border border-primary bg-primary text-white hover:bg-white hover:text-primary inline-flex items-center gap-2"
            >
              Enter the arena <ArrowRight size={14} />
            </Link>
            <Link
              data-testid="cta-feed"
              to="/feed"
              className="font-mono-acc text-xs px-6 py-4 border border-border hover:border-foreground inline-flex items-center gap-2"
            >
              Hear the feed
            </Link>
            <button
              data-testid="cta-trap"
              onClick={toggleAmbient}
              className={`font-mono-acc text-xs px-6 py-4 border inline-flex items-center gap-2 ${
                ambientOn
                  ? "border-secondary bg-secondary text-background"
                  : "border-secondary text-secondary hover:bg-secondary hover:text-background"
              }`}
            >
              {ambientOn ? <Pause size={14} /> : <Play size={14} />}
              {ambientOn ? "Trap loop live" : "Play trap beats"}
            </button>
          </div>

          {/* Live trap loop indicator */}
          {ambientOn && (
            <div
              data-testid="trap-now-playing"
              className="mt-6 inline-flex items-center gap-3 border border-secondary bg-secondary/5 px-4 py-2 max-w-full"
            >
              <span className="flex items-end gap-[2px] h-4">
                <span className={`eq-bar ${ambientPlaying && !ambientMuted ? "" : "opacity-30"}`} style={{ animationPlayState: ambientPlaying && !ambientMuted ? "running" : "paused" }} />
                <span className={`eq-bar ${ambientPlaying && !ambientMuted ? "" : "opacity-30"}`} style={{ animationPlayState: ambientPlaying && !ambientMuted ? "running" : "paused" }} />
                <span className={`eq-bar ${ambientPlaying && !ambientMuted ? "" : "opacity-30"}`} style={{ animationPlayState: ambientPlaying && !ambientMuted ? "running" : "paused" }} />
                <span className={`eq-bar ${ambientPlaying && !ambientMuted ? "" : "opacity-30"}`} style={{ animationPlayState: ambientPlaying && !ambientMuted ? "running" : "paused" }} />
              </span>
              <div className="min-w-0">
                <div className="font-mono-acc text-[9px] text-secondary">
                  // {ambientMuted ? "MUTED — TAP UNMUTE" : "NOW LOOPING"} · {ambientTrack.genre}
                </div>
                <div className="font-display text-sm truncate">{ambientTrack.title}</div>
              </div>
              {ambientMuted ? (
                <button
                  data-testid="unmute-btn"
                  onClick={unmuteAmbient}
                  className="font-mono-acc text-[10px] border border-secondary bg-secondary text-background px-3 py-2 hover:bg-background hover:text-secondary inline-flex items-center gap-1 ml-2"
                >
                  <Volume2 size={12} /> UNMUTE
                </button>
              ) : (
                <button
                  data-testid="trap-next"
                  onClick={nextAmbient}
                  className="font-mono-acc text-[9px] border border-border px-2 py-1 hover:border-secondary hover:text-secondary ml-2"
                >
                  Next ↻
                </button>
              )}
            </div>
          )}

          {/* Hero collage — legendary producer names over studio imagery */}
          <div className="mt-16 hidden md:grid grid-cols-6 gap-2 max-w-4xl" data-testid="hero-collage">
            {LEGENDS.map((l, i) => (
              <div
                key={l.name}
                className={`relative aspect-[3/4] overflow-hidden border border-border group ${
                  i % 2 === 0 ? "translate-y-0" : "translate-y-6"
                }`}
              >
                <img
                  src={l.image}
                  alt={l.name}
                  loading="lazy"
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
                <div className="absolute bottom-2 left-2 right-2">
                  <div className="font-mono-acc text-[8px] text-primary">#{String(i + 1).padStart(2, "0")}</div>
                  <div className="font-display text-[11px] leading-tight text-foreground truncate">{l.name}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Inspired-by strip */}
      <section className="border-b border-border bg-background">
        <div className="px-6 md:px-12 py-16 max-w-7xl mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="font-mono-acc text-xs text-secondary">// INSPIRED BY THE GREATS</div>
              <h2 className="font-display text-4xl md:text-5xl mt-3">
                Study the <span className="text-primary">blueprints</span>.
              </h2>
            </div>
            <Link
              data-testid="cta-hall"
              to="/hall-of-fame"
              className="font-mono-acc text-xs px-5 py-3 border border-secondary text-secondary hover:bg-secondary hover:text-background inline-flex items-center gap-2"
            >
              Hall of Fame <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="legends-strip">
            {LEGENDS.map((l, i) => (
              <div
                key={l.name}
                data-testid={`legend-strip-${i}`}
                className="border border-border bg-card group overflow-hidden"
              >
                <div className="aspect-square overflow-hidden">
                  <img
                    src={l.image}
                    alt={l.name}
                    loading="lazy"
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500 scale-105 group-hover:scale-110"
                  />
                </div>
                <div className="p-3 border-t border-border">
                  <div className="font-mono-acc text-[9px] text-muted-foreground">{l.era.split(" ")[0]}</div>
                  <div className="font-display text-base leading-tight">{l.name}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Numbers strip */}
      <section className="border-b border-border bg-card">
        <div className="px-6 md:px-12 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            ["01", "Drop a beat"],
            ["02", "Get votes"],
            ["03", "Win the bracket"],
            ["04", "Take the crown"],
          ].map(([n, l]) => (
            <div key={n} className="flex items-baseline gap-3">
              <span className="font-mono-acc text-xs text-muted-foreground">{n}</span>
              <span className="font-display text-2xl">{l}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Feature grid */}
      <section className="px-6 md:px-12 py-20 max-w-7xl mx-auto">
        <h2 className="font-display text-4xl md:text-5xl mb-12">Built for the <span className="text-primary">producer</span>.</h2>
        <div className="grid md:grid-cols-3 gap-0 border border-border">
          {[
            { icon: <Mic />, title: "Upload raw", desc: "Drop your mp3 / wav with BPM, key, genre. We host the audio so your SoundCloud stays clean." },
            { icon: <Trophy />, title: "Tournament brackets", desc: "Single elimination, 4 or 8 beats. Community votes decide who advances." },
            { icon: <Sparkles />, title: "AI cover art", desc: "Generate brutalist cover art for every beat with Gemini Nano Banana. One click." },
          ].map((f, i) => (
            <div key={i} className="p-8 border-r border-border last:border-r-0 border-b md:border-b-0">
              <div className="text-primary mb-6">{f.icon}</div>
              <h3 className="font-display text-2xl mb-3">{f.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Outro */}
      <section className="px-6 md:px-12 py-20 border-t border-border bg-card">
        <div className="max-w-3xl">
          <div className="font-mono-acc text-xs text-secondary mb-4">// CHAPTER 02</div>
          <p className="font-display text-4xl md:text-5xl leading-tight">
            Stop posting beats into the void. Put it in front of judges who actually press the play button.
          </p>
          <Link
            data-testid="cta-join-bottom"
            to="/register"
            className="mt-10 font-mono-acc text-xs px-6 py-4 border border-foreground bg-foreground text-background hover:bg-primary hover:border-primary hover:text-white inline-flex items-center gap-2"
          >
            Create account <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
